import React, { useState } from "react";
import { CheckCircleOutlined, HomeOutlined, LoadingOutlined, StopOutlined, WarningOutlined } from "@ant-design/icons";
import { Breadcrumb, Form, Input, Button, Space, Table, Modal, Tooltip, Spin } from "antd";
import type { FormInstance, TableColumnsType, TableProps } from "antd";
import "./item_wh_transfer_out.css";
import withTransferOutLogic from "../../components/hoc_wh_transfer_out";
import ModalDataTransferAccept from "./modal_data_transfer_accept";
import EditTableWhTransOut from "./edit_table_wh_transfer_out";
import LocationCodeWhOut from "./location_code_wh_transfer_out";
import { FaPlusCircle } from "react-icons/fa";
import { IoPrintSharp } from "react-icons/io5";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { HiOutlineDocumentCheck } from "react-icons/hi2";

interface DataType {
    key: React.Key;
    itemNumber: string;
    itemName: string;
    uom: string;
    locationCode?: string;
    qtyAvailable: number;
    qtyRequest: number;
    qtyOut: number;
}

interface SelectedData {
    requestNo: string;
    requestDate: string;
    fromWhCode: string;
    fromWhName: string;
    toWhCode: string;
    toWhName: string;
    purpose: string;
    requestBy: string;
    requestByName: string;
    approval: string;
    approvalByName: string;
    approvalDate: string;
    approvalRemark: string;
}

interface LocationData {
    key: string;
    locationCode: string;
    locationName: string;
}

interface ItemWHTransferOutProps {
    form: FormInstance;
    columns: TableColumnsType<DataType>;
    rowSelection: TableProps<DataType>["rowSelection"];
    dataSource: DataType[];
    isModalOpen: boolean;
    showModal: () => void;
    handleCancel: () => void;
    handleRowDoubleClick: (record: SelectedData) => void;
    isDataSelected: boolean;
    isEditModalOpen: boolean;
    handleEditModalCancel: () => void;
    handleTableauRowDoubleClick: (record: DataType) => void;
    editingRecord: DataType | null;
    isLocationModalOpen: boolean;
    showLocationModal: () => void;
    handleLocationModalCancel: () => void;
    currentWarehouseCode: string | null;
    handleLocationSelect: (record: LocationData) => void;
    handleEditFormSubmit: (values: any) => void;
    userWarehouseCode: string | undefined;
    handleCopyFromReceipt: () => void;
    handleDelete: () => void;
    autosaveStatus: 'idle' | 'processing' | 'error' | 'stopped';
    handleNew: () => void;
    handleSubmit: () => void;
    handleVoid: () => void;
    invalidRowKeys: React.Key[];
    isSubmitDisabled: boolean;
    isProcessing: boolean;
    handlePrint: () => void;
    isPrinting: boolean;
    isPrintDisabled: boolean;
    isReportModalOpen: boolean;
    handleReportModalCancel: () => void;
}

const ItemWHTransferOut: React.FC<ItemWHTransferOutProps> = ({
    form,
    columns,
    rowSelection,
    dataSource,
    isModalOpen,
    showModal,
    handleCancel,
    handleRowDoubleClick,
    isDataSelected,
    isEditModalOpen,
    handleEditModalCancel,
    handleTableauRowDoubleClick,
    editingRecord,
    isLocationModalOpen,
    showLocationModal,
    handleLocationModalCancel,
    currentWarehouseCode,
    handleLocationSelect,
    handleEditFormSubmit,
    userWarehouseCode,
    handleCopyFromReceipt,
    handleDelete,
    autosaveStatus,
    handleNew,
    handleSubmit,
    handleVoid,
    invalidRowKeys,
    isSubmitDisabled,
    isProcessing,
    handlePrint,
    isPrinting,
    isPrintDisabled,
    isReportModalOpen,
    handleReportModalCancel

}) => {
    const Printbtn = IoPrintSharp as React.ComponentType<any>;
    const Voidbtn = IoIosCloseCircleOutline as React.ComponentType<any>;
    const Submitbtn = HiOutlineDocumentCheck as React.ComponentType<any>;
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };
    const status = Form.useWatch('trans_out_status', form);
    return (
        <Spin spinning={isProcessing} size="large" tip="Loading...">
            <div id="iau9" style={transformStyle}>
                <div className="gjs-grid-row" id="i0pxj">
                    <div className="gjs-grid-column" id="imj0q">
                        <Breadcrumb
                            items={[
                                {
                                    title: <HomeOutlined />,
                                },
                                {
                                    href: "#/dashboard/data_wh_transfer_out",
                                    title: (
                                        <>
                                            <span>Data WH Transfer Out</span>
                                        </>
                                    ),
                                },
                                {
                                    title: "Item WH Transfers Out",
                                },
                            ]}
                        />
                    </div>
                </div>
                <div className="gjs-grid-row" id="r31dsa">
                    <div className="gjs-grid-column" id="erf45" >
                        <Button className="gr-button" onClick={handleNew} disabled={isProcessing}>
                            <FaPlusCircle style={{ width: '1em', height: '1em', marginRight: '8px' }} />
                            New
                        </Button>
                        <Button className="gr-button" onClick={handleSubmit} disabled={isSubmitDisabled || isProcessing}>
                            <Submitbtn style={{ width: '1em', height: '1em', marginRight: '8px' }} />
                            Submit
                        </Button>
                        <Button className="gr-button" onClick={handleVoid} disabled={status !== 'Draft' || isProcessing}>
                            <Voidbtn style={{ width: '1em', height: '1em', marginRight: '8px' }} />
                            Void
                        </Button>
                        <Button className="gr-button" onClick={handlePrint} loading={isPrinting} disabled={isPrintDisabled || isProcessing}>
                            <Printbtn style={{ width: '1em', height: '1em', marginRight: '8px' }} />
                            Print
                        </Button>
                        <Space style={{ marginLeft: '10px' }}>
                            {autosaveStatus === 'processing' && (
                                <Tooltip title="Autosave sedang berjalan...">
                                    <LoadingOutlined style={{ color: '#1890ff' }} />
                                </Tooltip>
                            )}
                            {autosaveStatus === 'error' && (
                                <Tooltip title="Autosave bermasalah. Periksa konsol untuk detail.">
                                    <WarningOutlined style={{ color: '#ff4d4f' }} />
                                </Tooltip>
                            )}
                            {autosaveStatus === 'stopped' && (
                                <Tooltip title="Autosave Stopped">
                                    <StopOutlined style={{ color: '#bfbfbf' }} />
                                </Tooltip>
                            )}
                            {autosaveStatus === 'idle' && (
                                <Tooltip title="Autosave berhasil.">
                                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                </Tooltip>
                            )}
                        </Space>
                    </div>
                </div>

                <div className="gjs-grid-row" id="ijb9y">
                    <div className="gjs-grid-column" id="i759h">
                        <Form
                            layout="horizontal"
                            style={{ maxWidth: 500 }}
                            labelCol={{ style: { width: '170px', textAlign: "left" } }}
                            wrapperCol={{ style: { width: '290px' } }}
                            className="custom-info-purchase-request"
                            form={form}
                        >
                            <Form.Item
                                label="Created"
                                name="created"
                                style={{
                                    marginBottom: "5px",
                                }}
                            >
                                <Input placeholder="" readOnly />
                            </Form.Item>
                            <Form.Item
                                label="Transfer Out Status"
                                name="trans_out_status"
                                style={{
                                    marginBottom: "5px",
                                }}
                            >
                                <Input placeholder="" readOnly />
                            </Form.Item>
                            <Form.Item
                                label="Transfer Out No"
                                name="trans_out_no"
                                style={{
                                    marginBottom: "5px",
                                }}
                            >
                                <Input placeholder="" readOnly />
                            </Form.Item>
                            <Form.Item
                                label="Transfer Out Date"
                                name="trans_out_date"
                                style={{
                                    marginBottom: "5px",
                                }}
                            >
                                <Input placeholder="" readOnly />
                            </Form.Item>
                            <Form.Item
                                label="Transfer Request No"
                                style={{ marginBottom: "5px", width: "500px" }} >
                                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                    <Form.Item
                                        name="trans_request_no"
                                        noStyle
                                        style={{ width: "100%" }}>
                                        <Input
                                            style={{ width: "100%" }}
                                            readOnly
                                        />
                                    </Form.Item>
                                    <Button
                                        type="primary"
                                        id="modal_data_transfer_accept"
                                        onClick={showModal}
                                        disabled={isDataSelected}
                                        icon={
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ width: "40px", height: "20px" }}>
                                                <path d="M11.625 16.5a1.875 1.875 0 1 0 0-3.75 1.875 1.875 0 0 0 0 3.75Z" />
                                                <path fillRule="evenodd" d="M5.625 1.5H9a3.75 3.75 0 0 1 3.75 3.75v1.875c0 1.036.84 1.875 1.875 1.875H16.5a3.75 3.75 0 0 1 3.75 3.75v7.875c0 1.035-.84 1.875-1.875 1.875H5.625a1.875 1.875 0 0 1-1.875-1.875V3.375c0-1.036.84-1.875 1.875-1.875Zm6 16.5c.66 0 1.277-.19 1.797-.518l1.048 1.048a.75.75 0 0 0 1.06-1.06l-1.047-1.048A3.375 3.375 0 1 0 11.625 18Z" clipRule="evenodd" />
                                                <path d="M14.25 5.25a5.23 5.23 0 0 0-1.279-3.434 9.768 9.768 0 0 1 6.963 6.963A5.23 5.23 0 0 0 16.5 7.5h-1.875a.375.375 0 0 1-.375-.375V5.25Z" />
                                            </svg>}
                                    />
                                </div>
                            </Form.Item>
                            <Form.Item
                                label="Transfer Request Date"
                                name="trans_request_date"
                                style={{
                                    marginBottom: "5px",
                                }}
                            >
                                <Input placeholder="" readOnly />
                            </Form.Item>
                            <div className="grid2">
                                <label style={{ fontWeight: 600 }}> Request From </label>
                                <Form.Item
                                    label="Warehouse Name"
                                    name="warehouse_name_req"
                                    style={{
                                        marginBottom: "5px",
                                    }}
                                >
                                    <Input placeholder="" readOnly />
                                </Form.Item>
                                <Form.Item
                                    label="Warehouse Code"
                                    name="warehouse_code_req"
                                    style={{
                                        marginBottom: "5px",
                                    }}
                                >
                                    <Input placeholder="" readOnly />
                                </Form.Item>
                            </div>
                            <div className="grid2">
                                <label style={{ fontWeight: 600 }}> Request To </label>
                                <Form.Item
                                    label="Warehouse Name"
                                    name="warehouse_name_to"
                                    style={{
                                        marginBottom: "5px",
                                    }}
                                >
                                    <Input placeholder="" readOnly />
                                </Form.Item>
                                <Form.Item
                                    label="Warehouse Code"
                                    name="warehouse_code_to"
                                    style={{
                                        marginBottom: "5px",
                                    }}
                                >
                                    <Input placeholder="" readOnly />
                                </Form.Item>
                                <Form.Item
                                    label="Purpose"
                                    name="purpose"
                                    style={{
                                        marginBottom: "5px",
                                    }}
                                >
                                    <Input.TextArea rows={4} placeholder="" readOnly />
                                </Form.Item>
                            </div>
                            <div className="grid2">
                                <label style={{ fontWeight: 600 }}> Approval </label>
                                <Form.Item
                                    label="Approval By"
                                    name="approval_by"
                                    style={{
                                        marginBottom: "5px",
                                    }}
                                >
                                    <Input placeholder="" readOnly />
                                </Form.Item>
                                <Form.Item
                                    label="Approval Date"
                                    name="approval_date"
                                    style={{
                                        marginBottom: "5px",
                                    }}
                                >
                                    <Input placeholder="" readOnly />
                                </Form.Item>
                                <Form.Item
                                    label="Approval Remark"
                                    name="approval_remark"
                                    style={{
                                        marginBottom: "5px",
                                    }}
                                >
                                    <Input.TextArea rows={4} placeholder="" readOnly />
                                </Form.Item>
                            </div>

                        </Form>
                    </div>
                </div>

                {/* Grid Row 3 */}
                <div className="gjs-grid-row" id="ijx5v">
                    <div className="gjs-grid-column" id="ipc7i">
                        <Table<DataType>
                            rowSelection={rowSelection}
                            columns={columns}
                            dataSource={dataSource}
                            scroll={{ x: 900 }}
                            rowClassName={(record) => invalidRowKeys.includes(record.key) ? 'row-error' : ''}
                            onRow={(record) => {
                                return {
                                    onDoubleClick: () => {
                                        handleTableauRowDoubleClick(record);
                                    },
                                };
                            }}
                        />
                        <div style={{ marginTop: "20px", textAlign: "left" }}>
                            <Space>
                                <Button
                                    type="primary"
                                    onClick={handleDelete}
                                    disabled={!rowSelection?.selectedRowKeys?.length}
                                    style={{
                                        height: "40px",
                                        width: "90px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                    icon={
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                            style={{ width: "22px", height: "22px" }}
                                        >
                                            <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z" clipRule="evenodd" />
                                        </svg>
                                    }
                                >
                                    Delete
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={handleCopyFromReceipt}
                                    disabled={!isDataSelected}
                                    style={{
                                        height: "40px",
                                        width: "190px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                    icon={
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                            style={{ width: "22px", height: "22px" }}
                                        >
                                            <path d="M7.5 3.375c0-1.036.84-1.875 1.875-1.875h.375a3.75 3.75 0 0 1 3.75 3.75v1.875C13.5 8.161 14.34 9 15.375 9h1.875A3.75 3.75 0 0 1 21 12.75v3.375C21 17.16 20.16 18 19.125 18h-9.75A1.875 1.875 0 0 1 7.5 16.125V3.375Z" />
                                            <path d="M15 5.25a5.23 5.23 0 0 0-1.279-3.434 9.768 9.768 0 0 1 6.963 6.963A5.23 5.23 0 0 0 17.25 7.5h-1.875A.375.375 0 0 1 15 7.125V5.25ZM4.875 6H6v10.125A3.375 3.375 0 0 0 9.375 19.5H16.5v1.125c0 1.035-.84 1.875-1.875 1.875h-9.75A1.875 1.875 0 0 1 3 20.625V7.875C3 6.839 3.84 6 4.875 6Z" />
                                        </svg>
                                    }
                                >
                                    Copy From Receipt
                                </Button>
                            </Space>
                        </div>
                    </div>
                </div>
                <Modal
                    title="Data Transfer Accept"
                    open={isModalOpen}
                    onCancel={handleCancel}
                    footer={null}
                    width={1000}
                >
                    <ModalDataTransferAccept onRowDoubleClick={handleRowDoubleClick} userWarehouseCode={userWarehouseCode} />
                </Modal>
                <Modal
                    title="Edit Item Details"
                    open={isEditModalOpen}
                    onCancel={handleEditModalCancel}
                    footer={null}
                    width={400}
                    className="modal-edit-table-wh-out"
                >
                    <EditTableWhTransOut
                        initialValues={editingRecord}
                        showLocationModal={showLocationModal}
                        onFinish={handleEditFormSubmit}
                        onCancel={handleEditModalCancel}
                    />
                </Modal>
                <Modal
                    title="Select Location Code"
                    open={isLocationModalOpen}
                    onCancel={handleLocationModalCancel}
                    footer={null}
                    width={800}
                >
                    <LocationCodeWhOut
                        warehouseCode={currentWarehouseCode}
                        itemCode={editingRecord?.itemNumber || null}
                        onRowDoubleClick={handleLocationSelect}
                    />
                </Modal>
                <Modal
                    title="Print Preview - WH Transfer Out"
                    open={isReportModalOpen}
                    onCancel={handleReportModalCancel}
                    footer={null}
                    width={1100}
                    destroyOnClose
                >
                    <div id="sti-viewer-container" style={{ width: '100%', minHeight: '75vh' }} />
                </Modal>
            </div>
        </Spin>
    );
};

export default withTransferOutLogic(ItemWHTransferOut);
